/**
 * 🔐 USE CLIENT AUTH - Hook d'authentification client
 * Gère la logique de connexion par téléphone, email et 2FA
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../services/api';
import { toast } from 'sonner';
// Note: Les sons et confettis sont gérés par ClientPortalPage.js via onLoginSuccess

export function useClientAuth({ onLoginSuccess, onHeartbeatsProfileLoaded }) {
  // Helper functions
  const getSavedPhone = () => {
    try {
      return localStorage.getItem('clientSavedPhone') || '';
    } catch {
      return '';
    }
  };

  // États de base
  const [phone, setPhone] = useState(getSavedPhone());
  const [hasStoredPhone, setHasStoredPhone] = useState(!!getSavedPhone());
  const [pin, setPin] = useState(['', '', '', '']);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [cards, setCards] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingSession, setIsCheckingSession] = useState(true);
  
  // États 2FA
  const [show2FAVerification, setShow2FAVerification] = useState(false);
  const [pending2FACustomer, setPending2FACustomer] = useState(null);
  const [totpStatus, setTotpStatus] = useState(null);
  
  // États Email Auth
  const [loginMethod, setLoginMethod] = useState('phone');
  const [email, setEmail] = useState('');
  const [emailCode, setEmailCode] = useState(['', '', '', '', '', '']);
  const [emailCodeSent, setEmailCodeSent] = useState(false);
  const [emailCooldown, setEmailCooldown] = useState(0);
  
  // Refs pour les inputs
  const pinRefs = useRef([]);
  const emailCodeRefs = useRef([]);
  
  // 🔧 FIX: Utiliser des refs pour les callbacks pour éviter les boucles de rendu
  const onHeartbeatsProfileLoadedRef = useRef(onHeartbeatsProfileLoaded);
  const onLoginSuccessRef = useRef(onLoginSuccess);
  
  // Mettre à jour les refs quand les callbacks changent
  useEffect(() => {
    onHeartbeatsProfileLoadedRef.current = onHeartbeatsProfileLoaded;
  }, [onHeartbeatsProfileLoaded]);
  
  useEffect(() => {
    onLoginSuccessRef.current = onLoginSuccess;
  }, [onLoginSuccess]);
  
  // 🔒 Ref pour éviter les doubles vérifications de session
  const sessionCheckedRef = useRef(false);

  // Vérifier la session au chargement (UNE SEULE FOIS)
  useEffect(() => {
    // 🔒 Éviter les vérifications multiples
    if (sessionCheckedRef.current) return;
    sessionCheckedRef.current = true;
    
    const checkSession = async () => {
      try {
        const savedCustomer = localStorage.getItem('lpeCustomer');
        if (savedCustomer) {
          const customerData = JSON.parse(savedCustomer);
          if (customerData.id) {
            const freshData = await api.getCustomer(customerData.id);
            if (freshData) {
              setCustomer(freshData);
              setIsLoggedIn(true);
              
              // 🔐 Charger les cartes séparément (important pour le coffre !)
              try {
                const cardsData = await api.getCustomerCards(freshData.id);
                console.log('🔐 Session - Cartes chargées:', cardsData?.length || 0);
                setCards(cardsData || []);
              } catch (cardsErr) {
                console.error('Erreur chargement cartes:', cardsErr);
                setCards([]);
              }
              
              // Charger le profil Heartbeats si callback fourni (utiliser la ref)
              if (onHeartbeatsProfileLoadedRef.current && freshData.heartbeatsProfile) {
                onHeartbeatsProfileLoadedRef.current(freshData.heartbeatsProfile);
              }
              
              // Vérifier le statut 2FA
              try {
                const status = await api.getTOTPStatus(freshData.id);
                setTotpStatus(status);
              } catch (err) {
                console.log('Pas de 2FA configuré');
              }
            }
          }
        }
      } catch (error) {
        console.log('Pas de session active:', error);
        localStorage.removeItem('lpeCustomer');
      } finally {
        setIsCheckingSession(false);
      }
    };
    
    checkSession();
  }, []); // 🔧 FIX: Pas de dépendances - s'exécute une seule fois au montage

  // Cooldown email
  useEffect(() => {
    if (emailCooldown > 0) {
      const timer = setTimeout(() => setEmailCooldown(emailCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [emailCooldown]);

  // Handler logout
  const handleLogout = useCallback(async () => {
    try {
      // Clear session token
      const sessionToken = localStorage.getItem('clientSessionToken');
      if (sessionToken) {
        try {
          await api.logout(sessionToken);
        } catch (e) {
          // Ignore errors on logout API call
        }
      }
      
      // Clear all states
      setIsLoggedIn(false);
      setCustomer(null);
      setCards([]);
      setPin(['', '', '', '']);
      setEmail('');
      setEmailCode(['', '', '', '', '', '']);
      setEmailCodeSent(false);
      
      // Clear ALL localStorage related to client session
      localStorage.removeItem('clientSessionToken');
      localStorage.removeItem('clientCustomer');
      localStorage.removeItem('lpeCustomer');
      localStorage.removeItem('heartbeatsProfile');
      localStorage.removeItem('guide_user_actions');
      localStorage.removeItem('guide_last_messages');
      localStorage.removeItem('clientPhone');
      localStorage.removeItem('clientSavedPhone');
      localStorage.removeItem('clientSession');
      
      toast.success('Déconnexion réussie 👋');
      
      // 🔄 FORCER LE RECHARGEMENT DE LA PAGE pour revenir au SAS
      // Cela garantit que le checkSession ne recharge pas l'ancien customer
      setTimeout(() => {
        window.location.reload();
      }, 500);
      
    } catch (error) {
      console.error('Erreur déconnexion:', error);
      toast.error('Erreur lors de la déconnexion');
    }
  }, []);

  // Handler PIN input
  const handlePinInput = useCallback((index, value) => {
    if (!/^\d?$/.test(value)) return;
    
    const newPin = [...pin];
    newPin[index] = value;
    setPin(newPin);
    
    if (value && index < 3 && pinRefs.current[index + 1]) {
      pinRefs.current[index + 1].focus();
    }
    
    if (newPin.every(d => d !== '')) {
      const pinCode = newPin.join('');
      handleLogin(null, pinCode);
    }
  }, [pin]);

  // Handler PIN keydown
  const handleKeyDown = useCallback((index, e) => {
    if (e.key === 'Backspace' && !pin[index] && index > 0 && pinRefs.current[index - 1]) {
      pinRefs.current[index - 1].focus();
    }
  }, [pin]);

  // Handler login principal
  const handleLogin = useCallback(async (e, pinCode = null) => {
    if (e) e.preventDefault();
    
    const finalPin = pinCode || pin.join('');
    if (!phone || phone.length < 10) {
      toast.error('Numéro de téléphone invalide');
      return;
    }
    if (finalPin.length !== 4) {
      toast.error('Code PIN à 4 chiffres requis');
      return;
    }
    
    setIsLoading(true);
    try {
      const result = await api.clientLogin(phone, finalPin);
      
      if (result.error) {
        toast.error(result.error);
        setPin(['', '', '', '']);
        if (pinRefs.current[0]) pinRefs.current[0].focus();
        return;
      }
      
      // Sauvegarder le numéro pour la prochaine fois
      localStorage.setItem('clientSavedPhone', phone);
      setHasStoredPhone(true);
      
      // Vérifier si 2FA est requis
      if (result.requires2FA) {
        setPending2FACustomer(result.customer);
        setShow2FAVerification(true);
        return;
      }
      
      // Connexion directe (pas de 2FA)
      // L'API retourne {success, customer} - les cartes doivent être chargées séparément
      const customerData = result.customer || result;
      
      setCustomer(customerData);
      setIsLoggedIn(true);
      localStorage.setItem('lpeCustomer', JSON.stringify(customerData));
      
      // 🔐 Charger les cartes séparément (l'API login ne les inclut pas)
      try {
        const cardsData = await api.getCustomerCards(customerData.id);
        console.log('🔐 Login - Cartes chargées:', cardsData?.length || 0);
        setCards(cardsData || []);
      } catch (cardsErr) {
        console.error('Erreur chargement cartes:', cardsErr);
        setCards([]);
      }
      
      // Callback de succès - la page gère les effets (sons, confettis, messages)
      // 🔧 FIX: Utiliser la ref pour éviter la dépendance dans useCallback
      if (onLoginSuccessRef.current) {
        onLoginSuccessRef.current(customerData);
      }
      
    } catch (error) {
      console.error('Erreur login détaillée:', error);
      console.error('Error message:', error.message);
      console.error('Error response:', error.response?.data);
      
      // Message d'erreur plus spécifique
      if (error.response?.status === 401) {
        toast.error('❌ Code PIN incorrect');
      } else if (error.response?.status === 404) {
        toast.error('📱 Numéro non enregistré');
      } else {
        toast.error('Erreur de connexion');
      }
      setPin(['', '', '', '']);
    } finally {
      setIsLoading(false);
    }
  }, [phone, pin]); // 🔧 FIX: Supprimé onLoginSuccess des dépendances

  // Handler envoi code email
  const handleSendEmailCode = useCallback(async (e) => {
    if (e) e.preventDefault();
    
    if (!email || !email.includes('@')) {
      toast.error('Email invalide');
      return;
    }
    
    setIsLoading(true);
    try {
      const result = await api.sendEmailCode(email);
      
      if (result.error) {
        toast.error(result.error);
        return;
      }
      
      setEmailCodeSent(true);
      setEmailCooldown(60);
      toast.success('Code envoyé ! Vérifie ta boîte mail 📧');
      
      // Focus sur le premier input du code
      setTimeout(() => {
        if (emailCodeRefs.current[0]) {
          emailCodeRefs.current[0].focus();
        }
      }, 100);
      
    } catch (error) {
      console.error('Erreur envoi code:', error);
      toast.error('Erreur lors de l\'envoi du code');
    } finally {
      setIsLoading(false);
    }
  }, [email]);

  // Handler input code email
  const handleEmailCodeInput = useCallback((index, value) => {
    if (!/^\d?$/.test(value)) return;
    
    const newCode = [...emailCode];
    newCode[index] = value;
    setEmailCode(newCode);
    
    if (value && index < 5 && emailCodeRefs.current[index + 1]) {
      emailCodeRefs.current[index + 1].focus();
    }
    
    if (newCode.every(d => d !== '')) {
      const codeValue = newCode.join('');
      handleVerifyEmailCode(null, codeValue);
    }
  }, [emailCode]);

  // Handler vérification code email
  const handleVerifyEmailCode = useCallback(async (e, codeValue = null) => {
    if (e) e.preventDefault();
    
    const finalCode = codeValue || emailCode.join('');
    if (finalCode.length !== 6) {
      toast.error('Code à 6 chiffres requis');
      return;
    }
    
    setIsLoading(true);
    try {
      const result = await api.verifyEmailCode(email, finalCode);
      
      if (result.error) {
        toast.error(result.error);
        setEmailCode(['', '', '', '', '', '']);
        if (emailCodeRefs.current[0]) emailCodeRefs.current[0].focus();
        return;
      }
      
      // Vérifier si 2FA est requis
      if (result.requires2FA) {
        setPending2FACustomer(result.customer);
        setShow2FAVerification(true);
        return;
      }
      
      // Connexion réussie
      setCustomer(result.customer);
      setIsLoggedIn(true);
      localStorage.setItem('lpeCustomer', JSON.stringify(result.customer));
      
      // 🔐 Charger les cartes séparément
      try {
        const cardsData = await api.getCustomerCards(result.customer.id);
        console.log('🔐 Email Login - Cartes chargées:', cardsData?.length || 0);
        setCards(cardsData || []);
      } catch (cardsErr) {
        console.error('Erreur chargement cartes:', cardsErr);
        setCards([]);
      }
      
      // ⚠️ Les sons et confettis sont gérés UNIQUEMENT par onLoginSuccess (completeLogin)
      // pour éviter les doublons de sons
      // 🔧 FIX: Utiliser la ref pour éviter la dépendance dans useCallback
      if (onLoginSuccessRef.current) {
        onLoginSuccessRef.current(result.customer);
      }
      
    } catch (error) {
      console.error('Erreur vérification:', error);
      toast.error('Erreur de vérification');
      setEmailCode(['', '', '', '', '', '']);
    } finally {
      setIsLoading(false);
    }
  }, [email, emailCode]); // 🔧 FIX: Supprimé onLoginSuccess des dépendances

  // Handler succès 2FA
  const handle2FASuccess = useCallback(async (customerData) => {
    setShow2FAVerification(false);
    setCustomer(customerData);
    setIsLoggedIn(true);
    localStorage.setItem('lpeCustomer', JSON.stringify(customerData));
    
    // 🔐 Charger les cartes séparément
    try {
      const cardsData = await api.getCustomerCards(customerData.id);
      console.log('🔐 2FA Success - Cartes chargées:', cardsData?.length || 0);
      setCards(cardsData || []);
    } catch (err) {
      console.error('Erreur chargement cartes:', err);
      setCards([]);
    }
    
    // ⚠️ Les sons et confettis sont gérés UNIQUEMENT par onLoginSuccess (completeLogin)
    // pour éviter les doublons de sons
    // 🔧 FIX: Utiliser la ref pour éviter la dépendance dans useCallback
    if (onLoginSuccessRef.current) {
      onLoginSuccessRef.current(customerData);
    }
  }, []); // 🔧 FIX: Supprimé onLoginSuccess des dépendances

  // Reset email form
  const resetEmailForm = useCallback(() => {
    setEmailCodeSent(false);
    setEmailCode(['', '', '', '', '', '']);
  }, []);

  return {
    // États
    phone,
    setPhone,
    hasStoredPhone,
    setHasStoredPhone,
    pin,
    setPin,
    isLoggedIn,
    setIsLoggedIn,
    customer,
    setCustomer,
    cards,
    setCards,
    isLoading,
    isCheckingSession,
    
    // États 2FA
    show2FAVerification,
    setShow2FAVerification,
    pending2FACustomer,
    setPending2FACustomer,
    totpStatus,
    setTotpStatus,
    
    // États Email
    loginMethod,
    setLoginMethod,
    email,
    setEmail,
    emailCode,
    setEmailCode,
    emailCodeSent,
    emailCooldown,
    
    // Refs
    pinRefs,
    emailCodeRefs,
    
    // Handlers
    handleLogout,
    handlePinInput,
    handleKeyDown,
    handleLogin,
    handleSendEmailCode,
    handleEmailCodeInput,
    handleVerifyEmailCode,
    handle2FASuccess,
    resetEmailForm,
  };
}

export default useClientAuth;
